// src/pages/AnnouncementsPage.jsx
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FaCalendar, FaUser, FaArrowRight, FaTrophy, FaUsers } from "react-icons/fa";
import { useNewPlayers } from "../hooks/usePlayers";
import { useAnnouncements } from "../hooks/useAnnouncements";
import PlayerPhoto from "../components/PlayerPhoto";

// ─── NEW MEMBERS ANNOUNCEMENT ────────────────────────────────────────────────
// Nobody maintains this list. Players appear here automatically for their first
// few days on the site and then drop off on their own, which is what stops
// long-standing members lingering under a "new" heading.
const NEW_MEMBER_DAYS = 5;

// ─── Carousel Component ───────────────────────────────────────────────────────
function NewMembersCarousel({ members }) {
  const getCardsToShow = () => {
    if (typeof window === "undefined") return 3;
    if (window.innerWidth >= 1024) return 3;
    if (window.innerWidth >= 640) return 2;
    return 1;
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsToShow, setCardsToShow] = useState(getCardsToShow);
  const [isPaused, setIsPaused] = useState(false);
  const cardsToShowRef = useRef(cardsToShow);

  // Keep ref in sync so the interval always sees the latest value
  useEffect(() => {
    cardsToShowRef.current = cardsToShow;
    // Clamp index when the viewport size or the member list changes
    setCurrentIndex(prev => Math.max(0, Math.min(prev, members.length - cardsToShow)));
  }, [cardsToShow, members.length]);

  // Responsive resize listener
  useEffect(() => {
    const handleResize = () => setCardsToShow(getCardsToShow());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-slide every 4 seconds, pauses on hover
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => {
        const max = members.length - cardsToShowRef.current;
        return prev >= max ? 0 : prev + 1;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, [isPaused, members.length]);

  const maxIndex = Math.max(0, members.length - cardsToShow);
  const goTo = (i) => setCurrentIndex(Math.max(0, Math.min(i, maxIndex)));
  const prev = () => setCurrentIndex(p => (p <= 0 ? maxIndex : p - 1));
  const next = () => setCurrentIndex(p => (p >= maxIndex ? 0 : p + 1));

  const cardWidthPct = 100 / cardsToShow;
  const translateX = -(currentIndex * cardWidthPct);

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Prev arrow */}
      <button
        onClick={prev}
        aria-label="Previous"
        className="absolute left-0 top-[42%] -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center bg-gray-900/90 border border-gray-700 hover:border-red-600 hover:bg-red-600/20 text-white rounded-full transition-all duration-200 shadow-lg"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Next arrow */}
      <button
        onClick={next}
        aria-label="Next"
        className="absolute right-0 top-[42%] -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center bg-gray-900/90 border border-gray-700 hover:border-red-600 hover:bg-red-600/20 text-white rounded-full transition-all duration-200 shadow-lg"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Sliding track — mx-10 keeps cards clear of arrows */}
      <div className="overflow-hidden mx-10">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(${translateX}%)` }}
        >
          {members.map((player) => (
            <div
              key={player.id}
              className="flex-shrink-0 px-2"
              style={{ width: `${cardWidthPct}%` }}
            >
              <Link
                to={`/roster/${player.id}`}
                className="group block bg-gray-900 border border-gray-800 rounded-lg overflow-hidden hover:border-red-600/50 transition-all duration-300 hover:shadow-lg hover:shadow-red-900/20"
              >
                {/* Photo */}
                <div className="aspect-square overflow-hidden bg-gray-800 relative">
                  <PlayerPhoto player={player} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {/* NEW badge */}
                  <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">
                    New
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <span className="text-red-500 text-xs font-semibold uppercase tracking-wider">
                    {player.division}
                  </span>
                  <h3 className="text-white font-bold text-lg mt-1 group-hover:text-red-400 transition-colors">
                    {player.name}
                  </h3>
                  <p className="text-gray-500 text-sm mt-1">{player.country}</p>
                  <p className="text-gray-600 text-xs mt-3 uppercase tracking-wide">
                    View Profile →
                  </p>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center items-center gap-2 mt-5">
        {Array.from({ length: maxIndex + 1 }).map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`rounded-full transition-all duration-300 ${
              i === currentIndex
                ? "bg-red-600 w-5 h-2"
                : "bg-gray-600 hover:bg-gray-400 w-2 h-2"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const tabs = ["All", "News", "Tournaments"];

function AnnouncementsPage() {
  const [activeTab, setActiveTab] = useState("All");

  // Content is managed from the admin panel; the hook falls back to the
  // original bundled announcements if Firestore is empty or unreachable.
  // "All" shows news first, then tournaments — each section keeps its own
  // running order from the admin panel.
  const { news, tournaments, featured } = useAnnouncements();

  const filtered = (activeTab === "All"
    ? [...news, ...tournaments]
    : activeTab === "News"
    ? news
    : tournaments
  ).filter(i => !i.featured)
   .sort((a, b) => {
     if (a.type !== "tournament" || b.type !== "tournament") return 0;
     if (a.eventStatus === b.eventStatus) return 0;
     return a.eventStatus === "upcoming" ? -1 : 1;
   });

  // Show banner only while within the 72-hour window
  // Anyone added to the roster in the last few days, newest first
  const { players: newMembers } = useNewPlayers(NEW_MEMBER_DAYS);
  const showNewMembers = newMembers.length > 0;

  return (
    <main className="bg-black min-h-screen">
      {/* Hero */}
      <section className="relative py-24 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 to-black"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight text-white mb-6">
            <span className="text-red-600">Announcements</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Latest news, tournament results, and updates from Prime Valor.
          </p>
        </div>
      </section>

      {/* Featured news card */}
      {featured && activeTab !== "Tournaments" && (
        <section className="px-6 pb-4">
          <div className="max-w-6xl mx-auto">
            <div className="group relative bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-red-600/50 transition-all duration-300">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="aspect-video md:aspect-auto overflow-hidden bg-gray-800">
                  {featured.image ? (
                    <img
                      src={featured.image}
                      alt={featured.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full min-h-[200px] bg-gradient-to-br from-gray-800 to-gray-900" />
                  )}
                </div>
                <div className="p-8 flex flex-col justify-center overflow-y-auto">
                  <div className="mb-4">
                    <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold uppercase rounded">Featured</span>
                    <span className="ml-2 text-red-500 text-sm uppercase tracking-wide">{featured.category}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-red-500 transition-colors">
                    {featured.title}
                  </h2>
                  {featured.body ? (
                    <div className="text-gray-400 mb-6 space-y-3">
                      {featured.body.split("\n\n").map((para, i) => (
                        <p key={i}>{para}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 mb-6">{featured.excerpt}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-2"><FaCalendar className="text-red-600" />{featured.date}</span>
                    <span className="flex items-center gap-2"><FaUser className="text-red-600" />{featured.author}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* New Members carousel — auto-expires after 72 hours */}
      {showNewMembers && activeTab !== "Tournaments" && (
        <section className="px-6 pb-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold uppercase rounded-full tracking-wider">
                  New
                </span>
                <h2 className="text-lg md:text-xl font-bold text-white uppercase tracking-wide">
                  Welcome, New Members!
                </h2>
              </div>
              <NewMembersCarousel members={newMembers} />
            </div>
          </div>
        </section>
      )}

      {/* Tabs */}
      <section className="sticky top-[72px] z-40 bg-gray-950/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex gap-2">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-full text-sm font-semibold uppercase tracking-wide transition-all duration-300 ${
                  activeTab === tab
                    ? "bg-red-600 text-white"
                    : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map(item => (
              <div
                key={item.id}
                className="group bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-red-600/50 transition-all duration-300"
              >
                <div className="aspect-video overflow-hidden relative bg-gray-800">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900" />
                  )}
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                      item.type === "tournament"
                        ? item.eventStatus === "upcoming"
                          ? "bg-green-600 text-white"
                          : "bg-gray-700 text-gray-300"
                        : "bg-red-600 text-white"
                    }`}>
                      {item.type === "tournament" ? item.eventStatus : item.category}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-red-500 transition-colors line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{item.excerpt}</p>

                  {item.type === "tournament" ? (
                    <div className="space-y-1 mb-4">
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <FaTrophy className="text-red-600" />
                        <span>Prize: {item.prizePool} · {item.format}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <FaUsers className="text-red-600" />
                        <span>{item.game}</span>
                      </div>
                    </div>
                  ) : null}

                  <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-800">
                    <span className="flex items-center gap-1">
                      <FaCalendar className="text-red-600" />{item.date}
                    </span>
                    {item.type === "news" && (
                      <button className="flex items-center gap-1 text-red-500 hover:text-red-400 font-semibold uppercase tracking-wide group/btn">
                        Read <FaArrowRight className="text-xs group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No items found.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-gradient-to-r from-red-900/20 to-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Stay in the Loop</h2>
          <p className="text-gray-400 mb-8">
            Follow us on social media and join our Discord for real-time updates.
          </p>
          <a
            href="https://discord.gg/SGXXCMpCxf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide rounded transition-colors"
          >
            Join Discord
          </a>
        </div>
      </section>
    </main>
  );
}

export default AnnouncementsPage;
